import { computed, ComputedRef, ref, Ref, watch } from 'vue';
import { infuse } from '../../ioc-config';
import { useUserContext } from '../../composables/user-context.composable';
import { IFcisWidgetParameters } from 'fcis-widget/dist/types/interfaces/IInitializationConfig';
import { CONTEXT_TYPES } from '@dh-io-cscs/user-context-manager/dist/common/constants';
import { ProductPriceResponseDto } from '@dh-io-pls/pls-store';
import { usePostCode } from '../../composables/post-code.composable';
import { ICustomer, IVehicle } from 'fcis-widget/dist/types/interfaces/ICalculationModel';
import {
  findPriceBreakDownItem,
  getFcisCustomerType,
  getPlsPriceData,
  getRequestMode,
  getWheelsData,
} from './finance.utils';
import { B2X_ACTIVE_PROFILE_CONNECTION_NAME, B2XActiveProfileState } from '@dh-io-mhb2b/active-profile';
import { initializeStore } from '@seamless/store';
import { Logger } from '../../utils/logger.util';
import { getProductDetailParam, ProductDetail } from '@dh-io-emh/commerce-api/src/models/product';
import { WheelOrderEntry } from '../../types/wheels.type';
import Vehicle from '../../models/vehicle.model';

const store = initializeStore();
const activeCustomerGroupId = ref<string>();

// TODO: figure out why userContext updates after customerGroup
const activeProfileCustomerType = ref<CONTEXT_TYPES>();

store.subscribe(B2X_ACTIVE_PROFILE_CONNECTION_NAME, (newActiveProfile: B2XActiveProfileState) => {
  activeCustomerGroupId.value = newActiveProfile?.activeCustomerGroup?.domainRef?.id;
  activeProfileCustomerType.value = newActiveProfile?.activeCustomerGroup?.customerType?.businessType as CONTEXT_TYPES;
});

const getNetExShowroomPrice = (plsData: ProductPriceResponseDto | void) => {
  if (!plsData) {
    throw new Error('getNetExShowroomPrice()::plsData is undefined');
  }

  const priceBreakDown = findPriceBreakDownItem(plsData as ProductPriceResponseDto, 'NET_EX_SHOWROOM');

  if (!priceBreakDown?.amount) {
    throw new Error('getNetExShowroomPrice()::NET_EX_SHOWROOM does not exist');
  }

  return parseFloat(priceBreakDown.amount);
};

export const useFcisWidget = <T extends Vehicle | ProductDetail>({
  product,
  initialize = ref(true),
  financialCode,
  customerType,
  isSummaryWidget,
  postCode,
  wheels,
  emit,
}: {
  product: Ref<T | undefined>;
  initialize?: Ref<boolean> | ComputedRef<boolean>;
  financialCode: Ref<string | undefined>;
  customerType: Ref<CONTEXT_TYPES | undefined>;
  isSummaryWidget: boolean;
  postCode: Ref<string | undefined>;
  wheels: Ref<WheelOrderEntry[]>;
  emit: (event: 'fcis-quote' | 'fcis-error' | 'loading', ...args: any[]) => void;
}) => {
  const userContext = useUserContext();
  const { postCode: regionPickerPostCode } = usePostCode();
  const { shopCountry, locale, fincalc, tracking, apiMode, b2xSwitch, lang } = infuse('config');

  const caller = isSummaryWidget ? fincalc.callerSummary : fincalc.callerCalculator;

  const fcisParameters = ref<IFcisWidgetParameters>();

  // This computed needed to avoid watching context for markets with active profile support
  const activeCustomerGroupOrUserContext = computed(() => {
    return activeCustomerGroupId.value || userContext.value;
  });

  const emitErrorEvent = (event: any) => {
    emit('fcis-error', event);
  };

  const emitLoadEvent = () => emit('loading');

  const setFcisWidgetParameters = async () => {
    if (!product.value || !initialize.value) return;

    try {
      // customerType can be optionally provided separately from B2X toggle,
      // e.g. via PCP option, which takes precedence over B2X user context
      const _customerType = customerType.value || activeProfileCustomerType.value || userContext.value;

      const requestMode = getRequestMode(product.value);

      const vehicle: IVehicle = {
        condition: { condition: 'new' },
        financialCode: financialCode.value,
      };

      const customer: ICustomer = {};

      const fcisParams: IFcisWidgetParameters = {
        market: shopCountry,
        caller,
        locale,
        vehicle,
        customer,
      };

      if (b2xSwitch.enable) {
        // Note: adding customer param will also hide the customer type dropdown from the widget
        customer.type = getFcisCustomerType(_customerType);
      }

      let plsData: ProductPriceResponseDto | void;

      if (fincalc.waitPLC) {
        const authService = infuse('authService');
        const { jweToken } = await authService.getAuthState();

        plsData = await getPlsPriceData({
          product: product.value,
          shopCountry,
          lang,
          apiMode,
          customerType: _customerType,
          postCode: postCode.value ?? regionPickerPostCode.value,
          activeCustomerGroupId: activeCustomerGroupId.value,
          jweToken,
        });
      }

      switch (requestMode) {
        case 'overseas': {
          vehicle.technicalIdReference = getProductDetailParam(product.value, 'vehicleId');

          let rawValue: number;

          if (fincalc.widgetParameters.usePlsNetExShowroomPrice) {
            rawValue = getNetExShowroomPrice(plsData);
          } else {
            rawValue = plsData?.purchasePrice.amount
              ? parseFloat(plsData.purchasePrice.amount)
              : product.value.price.value;
          }

          vehicle.prices = [
            {
              id: 'purchasePrice',
              currency: product.value.price.currencyIso,
              rawValue,
              // any cast because 'charges' is optional but typed as mandatory
              charges: undefined as any,
            },
          ];

          break;
        }
        default:
          fcisParams.vehicleId = getProductDetailParam(product.value, 'commissionNumber');

          if (wheels.value?.length) {
            vehicle.vehicleConfiguration = {
              ...vehicle.vehicleConfiguration,
              equipments: [getWheelsData(wheels.value)],
            };
          }

          // Use additional values, prices and vehicle configuration from PLC if available and configured to do so
          if (fincalc.widgetParameters.useAdditionalParams && plsData) {
            fcisParams.vehicle = { ...vehicle, ...((plsData.financeLeasingInfo as any)?.vehicle as IVehicle) };
            fcisParams.customer = { ...customer, ...(plsData.financeLeasingInfo as any)?.customer };
          }

          break;
      }

      fcisParameters.value = fcisParams;

      emitLoadEvent();
    } catch (err) {
      emitErrorEvent(err);
      Logger.error('finance.composable::setFcisWidgetParameters()', err);
    }
  };

  watch(
    [
      activeCustomerGroupOrUserContext,
      product,
      financialCode,
      customerType,
      initialize,
      postCode,
      regionPickerPostCode,
    ],
    setFcisWidgetParameters,
    {
      immediate: true,
    },
  );

  return { caller, environment: fincalc.environment, parentApplicationId: tracking.id, fcisParameters };
};
